import { type ReactNode, type ReactElement } from 'react';
import Link from 'next/link';

import { TabOrientation } from '@/lib/types';
import { alpha } from '@mui/material/styles';
import { blue } from '@/lib/colours';
import { isNullOrUndefined } from '@/lib/comparison';
import { EventType } from '@/lib/types';

import { Box, Chip, Typography } from '@/primitives';
import { Tooltip } from '../Tooltip';
import type { ChipOwnProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

export interface TabDescriptor<T> {
  badgeContent?: ReactNode;
  icon?: ReactNode;
  label: ReactNode;
  value: T;
  disabled?: boolean;
  /** Shown on hover (e.g. explain why a tab is disabled). */
  tooltip?: ReactNode;
  /** Datadog RUM custom action name for user interaction tracking */
  ddActionName?: string;
}

export interface TabsProps<T> {
  getHref?: (value: T) => string;
  onClick?: (value: T) => void;
  orientation?: TabOrientation;
  tabs: Array<TabDescriptor<T>>;
  value: string;
  trackTabChange?: (event: any) => void;
  trackEventName?: string;
  eventType?: EventType;
  tabButtonProps?: ChipOwnProps;
}

export function Tabs<T>({
  getHref,
  onClick,
  orientation,
  tabs,
  value,
  trackTabChange,
  trackEventName,
  tabButtonProps,
  eventType = EventType.TAB_SWITCH,
}: TabsProps<T>) {
  const handleClick = (tabValue: T) => {
    const meta = tabs.find((t) => t.value === tabValue);
    if (meta?.disabled) {
      return;
    }
    if (trackTabChange && trackEventName) {
      trackTabChange({
        name: trackEventName,
        properties: {
          eventType,
          selectedTab: tabValue,
        },
      });
    }
    if (onClick) {
      onClick(tabValue);
    }
  };

  const wrapTab = (node: ReactElement, tab: TabDescriptor<T>) => {
    if (tab.tooltip == null || tab.tooltip === '') {
      return node;
    }
    return (
      <Tooltip title={tab.tooltip}>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            ...(tab.disabled ? { cursor: 'not-allowed' } : null),
          }}
        >
          {node}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: orientation,
        gap: '8px',
      }}
    >
      {tabs.map((tab, index) => {
        const isSelected = tab.value === value;
        const ddActionName = tab.ddActionName ?? `Tab: ${String(tab.value)}`;
        const chip = (
          <Chip
            {...tabButtonProps}
            data-dd-action-name={ddActionName}
            disabled={tab.disabled}
            aria-current={isSelected}
            className={isSelected ? 'selectedTab' : ''}
            label={
              <>
                {tab.icon != null && (
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    {tab.icon}
                  </Box>
                )}
                <Typography variant="labellarge">{tab.label}</Typography>
                {!isNullOrUndefined(tab.badgeContent) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'action.hover',
                      borderRadius: '28px',
                      height: '18px',
                      minWidth: '18px',
                      padding: '2px',
                    }}
                  >
                    <Typography variant="labelsmall">
                      {tab.badgeContent}
                    </Typography>
                  </Box>
                )}
              </>
            }
            onClick={getHref ? undefined : () => handleClick(tab.value)}
            component={getHref ? 'div' : undefined}
            sx={(theme) => ({
              '& .MuiChip-label': {
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '0',
              },
              backgroundColor: isSelected ? blue[100] : 'transparent',
              color: isSelected ? 'primary.main' : 'text.secondary',
              padding: '24px',
              ...(isSelected &&
                theme.applyStyles('dark', {
                  backgroundColor: alpha(blue[500], 0.22),
                })),
              ...(orientation === TabOrientation.Vertical && {
                justifyContent: 'flex-start',
                width: '100%',
              }),
              ...(tabButtonProps?.sx as Exclude<
                SxProps<Theme>,
                ReadonlyArray<unknown>
              >),
            })}
          />
        );
        if (getHref) {
          const href = getHref(tab.value);
          return (
            <Link
              key={index}
              href={href}
              data-dd-action-name={ddActionName}
              style={{ textDecoration: 'none' }}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                if (e.ctrlKey || e.metaKey || e.button !== 0) return;
                e.preventDefault();
                handleClick(tab.value);
              }}
            >
              {wrapTab(chip, tab)}
            </Link>
          );
        }
        return <Box key={index}>{wrapTab(chip, tab)}</Box>;
      })}
    </Box>
  );
}
